import './LinkInvestmentsModal.scss';

import { startCase, toLower, isEmpty, capitalize } from 'lodash';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import AccountsTableByType from 'components/shared/AccountsTableByType/AccountsTableByType';
import BaseModal from 'components/shared/Modal/BaseModal';
import {
  getAddAccountsOrderedByTypeName,
  getDeleteAccountsOrderedByTypeName,
  getBankAccountToUpdate,
  getAddUpdatedAssociations,
  getDeleteUpdatedAssociations,
  getBankModalState
} from 'state/linkedBankAccounts/LinkedBankAccountsSelectors';
import {
  changeBankModalState,
  clearDeleteAssociations,
  clearAddAssociations
} from 'state/linkedBankAccounts/LinkedBankAccountsActions';
import Store from 'state/Store';
import { updateExistingBankAssociations } from 'services/LinkedBankAccounts';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import { getEligibleAgreementsForBank } from 'services/Rest';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import PageLoadingSpinner from 'components/layouts/Navigation/PageLoadingSpinner';

class LinkInvestmentsModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true
    };
  }

  componentDidMount() {
    const {
      bankAccountToUpdate: { bankAgreementKey }
    } = this.props;
    getEligibleAgreementsForBank(bankAgreementKey);
  }

  componentDidUpdate(prevProps) {
    const { requestStatus } = this.props;
    if (
      requestStatus.eligibleAgreementsForBank === REQUEST_STATES.received &&
      prevProps.requestStatus.eligibleAgreementsForBank !==
        REQUEST_STATES.received
    ) {
      this.setState({ isLoading: false });
    }

    if (
      requestStatus.eligibleAgreementsForBank === REQUEST_STATES.error &&
      prevProps.requestStatus.eligibleAgreementsForBank !== REQUEST_STATES.error
    ) {
      this.setState({ isLoading: false });
    }
  }

  handleSaveAdd = () => {
    const { handleSaveAndClose, bankAccountToUpdate, updatedAddAssociations } =
      this.props;

    const { bankAgreementKey, bankAssociations } = bankAccountToUpdate;

    const bank = {
      bankAgreementKey,
      bankAssociations
    };

    updateExistingBankAssociations(
      bank,
      updatedAddAssociations,
      true,
      handleSaveAndClose
    );
  };

  handleSaveDelete = () => {
    const {
      handleSaveAndClose,
      bankAccountToUpdate,
      updatedDeleteAssociations
    } = this.props;

    const { bankAgreementKey, bankAssociations } = bankAccountToUpdate;

    const bank = {
      bankAgreementKey,
      bankAssociations
    };

    updateExistingBankAssociations(
      bank,
      updatedDeleteAssociations,
      false,
      handleSaveAndClose
    );
  };

  shouldDisableSave = () => {
    const { addAccounts, deleteAccounts } = this.props;
    const accountsAdded = addAccounts
      .map((a) => a.accountDescriptor)
      .some((account) => account.checked);
    const accountsDeleted = deleteAccounts
      .map((a) => a.accountDescriptor)
      .some((account) => account.checked);
    return !accountsAdded && !accountsDeleted;
  };

  handleAddState = () => {
    Store.dispatch(changeBankModalState('add'));
    Store.dispatch(clearDeleteAssociations());
  };

  handleDeleteState = () => {
    Store.dispatch(changeBankModalState('delete'));
    Store.dispatch(clearAddAssociations());
  };

  render() {
    const {
      addAccounts = [],
      deleteAccounts = [],
      handleCloseUpdate,
      bankAccountToUpdate,
      onUpdateAddAssociation,
      onUpdateDeleteAssociation,
      handleSelectAllAdd,
      handleSelectAllDelete,
      handleDeselectAllAdd,
      handleDeselectAllDelete,
      bankModalState
    } = this.props;
    const { isLoading } = this.state;
    if (!bankAccountToUpdate) {
      return null;
    }

    const {
      bankName = '',
      accountNumber = '',
      accountTypeName = '',
      bankRestrictions
    } = bankAccountToUpdate;
    const transformedName = startCase(toLower(bankName));
    const isAdd = bankModalState === 'add';
    const disableSave = this.shouldDisableSave();
    const addButtonClassname = isAdd
      ? 'modal-state-button-add selected'
      : 'modal-state-button-add';
    const deleteButtonClassname = !isAdd
      ? 'modal-state-button-delete selected'
      : 'modal-state-button-delete';

    if (isLoading) {
      return (
        <BaseModal
          isOpen
          hideAnchor
          close={handleCloseUpdate}
          disableClickOff
          title="Link Investments With Your Bank Account"
          containerClassName="link-investments-modal-container">
          <PageLoadingSpinner name="Eligible Agreements" />;
        </BaseModal>
      );
    }

    if (!isLoading && isAdd && isEmpty(addAccounts)) {
      return (
        <BaseModal
          isOpen
          hideAnchor
          close={handleCloseUpdate}
          disableClickOff
          title="Link Investments With Your Bank Account"
          closeTitle=""
          containerClassName="link-investments-modal-container">
          <div className="bank-title-container">
            <div className="bank-title">
              {bankName}
              <p className="bank-type">{capitalize(accountTypeName)}</p>
            </div>
          </div>
          <div className="modal-state-buttons-container">
            <button
              onClick={this.handleAddState}
              className={addButtonClassname}>
              Add
            </button>
            <button
              onClick={this.handleDeleteState}
              className={deleteButtonClassname}>
              Delete
            </button>
          </div>
          <div className="no-accounts-text">
            You have no eligible accounts to add to this bank.
          </div>
        </BaseModal>
      );
    }

    if (!isLoading && !isAdd && isEmpty(deleteAccounts)) {
      return (
        <BaseModal
          isOpen
          hideAnchor
          close={handleCloseUpdate}
          disableClickOff
          title="Link Investments With Your Bank Account"
          containerClassName="link-investments-modal-container">
          <div className="modal-state-buttons-container">
            <button
              onClick={this.handleAddState}
              className={addButtonClassname}>
              Add
            </button>
            <button
              onClick={this.handleDeleteState}
              className={deleteButtonClassname}>
              Delete
            </button>
          </div>
          <div className="no-accounts-text">
            You have no eligible accounts to delete from this bank.
          </div>
        </BaseModal>
      );
    }

    return (
      <BaseModal
        isOpen
        hideAnchor
        close={handleCloseUpdate}
        disableClickOff
        title="Link Investments With Your Bank Account"
        containerClassName="link-investments-modal-container">
        <Fragment>
          <div className="link-investments-modal-header">
            Select the investment(s) you want to manage using your{' '}
            <b>{transformedName}</b> {accountNumber} <b>account</b>.
          </div>
          <div className="bank-title-container">
            <div className="bank-title">
              {bankName}
              <p className="bank-type">{capitalize(accountTypeName)}</p>
            </div>
            <div className="select-buttons-container">
              <button
                className="select-all-button"
                onClick={
                  isAdd
                    ? () => handleSelectAllAdd()
                    : () => handleSelectAllDelete()
                }>
                Select All
              </button>
              <button
                className="select-all-button"
                onClick={
                  isAdd
                    ? () => handleDeselectAllAdd()
                    : () => handleDeselectAllDelete()
                }>
                Deselect All
              </button>
            </div>
          </div>
          <div className="modal-state-buttons-container">
            <button
              onClick={this.handleAddState}
              className={addButtonClassname}>
              Add
            </button>
            <button
              onClick={this.handleDeleteState}
              className={deleteButtonClassname}>
              Delete
            </button>
          </div>
          <AccountsTableByType
            accounts={isAdd ? addAccounts : deleteAccounts}
            bankRestrictions={bankRestrictions}
            leftColTitle={''}
            rightColTitle={'Account Number'}
            includeCheckbox={true}
            handleCheckUpdate={
              isAdd ? onUpdateAddAssociation : onUpdateDeleteAssociation
            }
            initialVisible={isAdd ? addAccounts.length : deleteAccounts.length}
          />
          <div className="link-investments-modal-buttons-container">
            <button
              className="cancel-button"
              id="cancel-bank-associations"
              onClick={handleCloseUpdate}>
              Cancel
            </button>
            <SaveLoadingButtonWithPermissions
              className="bank-save-button"
              id="save-bank-associations"
              disabled={disableSave}
              onClick={isAdd ? this.handleSaveAdd : this.handleSaveDelete}
              requestStatusKey={isAdd ? 'updateBankAssociations' : 'deleteBank'}
              shouldDisable>
              Save
            </SaveLoadingButtonWithPermissions>
          </div>
        </Fragment>
      </BaseModal>
    );
  }
}

const mapStateToProps = (state) => ({
  addAccounts: getAddAccountsOrderedByTypeName(state) || [],
  deleteAccounts: getDeleteAccountsOrderedByTypeName(state) || [],
  bankAccountToUpdate: getBankAccountToUpdate(state),
  updatedAddAssociations: getAddUpdatedAssociations(state),
  updatedDeleteAssociations: getDeleteUpdatedAssociations(state),
  requestStatus: getRequestStatus(state),
  bankModalState: getBankModalState(state)
});

export default connect(mapStateToProps)(LinkInvestmentsModal);
